import { z } from "zod";
import {
  cursorPaginatedResponseSchema,
  DateSchema,
  IdSchema,
  KaratSchema,
  NonNegativeNumber,
  NoteSchema,
} from "./commonSchema";
import type { CursorPaginatedResponse } from "@/types/common";
import { MAX_NAME_LENGTH } from "@/consts";

export const TransactionItemSchema = z.object({
  name: z.string().max(MAX_NAME_LENGTH, "errors.validation.item.nameTooLong"),
  declaredKarat: z.coerce
    .number({ required_error: "errors.validation.item.declaredKaratRequired" })
    .pipe(KaratSchema),
  testedKarat: KaratSchema.nullish(),
  rawWeight: z.coerce
    .number({ required_error: "errors.validation.item.rawWeightRequired" })
    .positive("errors.validation.item.rawWeightInvalid"),
  returned: z.boolean(),
});

export type TransactionItemForm = z.infer<typeof TransactionItemSchema>;

export const CreateTransactionSchema = z
  .object({
    type: z.enum(["inbound", "outbound"]),
    clientId: IdSchema,
    laborCost: z.coerce
      .number({ invalid_type_error: "errors.validation.laborCost.invalidNumber" })
      .min(0, "errors.validation.laborCost.mustBePositive"),
    totalLabor: z.coerce
      .number({ invalid_type_error: "errors.validation.totalLabor.invalidNumber" })
      .min(0, "errors.validation.totalLabor.mustBePositive"),
    deposit: z.coerce
      .number({ invalid_type_error: "errors.validation.deposit.invalidNumber" })
      .min(0, "errors.validation.deposit.mustBePositive"),
    pureWeight: z.coerce
      .number({ invalid_type_error: "errors.validation.pureWeight.invalidNumber" })
      .min(0, "errors.validation.pureWeight.mustBePositive"),
    pureWeightAdded: z.coerce
      .number({ invalid_type_error: "errors.validation.pureWeightAdded.invalidNumber" })
      .min(0, "errors.validation.pureWeightAdded.mustBePositive"),
    addWeightLaborCost: z.coerce
      .number({ invalid_type_error: "errors.validation.addWeightLaborCost.invalidNumber" })
      .min(0, "errors.validation.addWeightLaborCost.mustBePositive"),
    items: z.array(TransactionItemSchema),
    note: NoteSchema,
  })
  .superRefine((data, ctx) => {
    if (data.type === "outbound") {
      const hasItems = data.items.length > 0;
      if (!hasItems && data.pureWeightAdded === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "errors.validation.transaction.outbound.blank",
          path: ["items"],
        });
      }
      if (data.pureWeightAdded > 0 && data.addWeightLaborCost <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "errors.validation.transaction.outbound.laborCostRequired",
          path: ["addWeightLaborCost"],
        });
      }
      for (let i = 0; i < data.items.length; i++) {
        if (data.items[i].testedKarat != null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "errors.validation.transaction.outbound.testedKaratNotAllowed",
            path: ["items", i, "testedKarat"],
          });
        }
        if (data.items[i].returned) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "errors.validation.transaction.outbound.returnedNotAllowed",
            path: ["items", i, "returned"],
          });
        }
      }
    }

    if (data.type === "inbound") {
      const hasItems = data.items.length > 0;
      if (!hasItems && data.deposit === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "errors.validation.transaction.inbound.blank",
          path: ["items"],
        });
      }
      if (hasItems && data.laborCost <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "errors.validation.transaction.inbound.laborCostRequired",
          path: ["laborCost"],
        });
      }
    }
  });

export type CreateTransactionForm = z.infer<typeof CreateTransactionSchema>;

export const PatchTransactionSchema = z.object({
  items: z
    .array(TransactionItemSchema)
    .min(1, "errors.validation.transaction.itemsRequired"),
});

export type PatchTransactionForm = z.infer<typeof PatchTransactionSchema>;

export const TransactionSchema = z.object({
  id: IdSchema,
  workshopId: IdSchema,
  clientId: IdSchema,
  type: z.enum(["inbound", "outbound"]),
  laborCost: NonNegativeNumber,
  addWeightLaborCost: NonNegativeNumber,
  totalLabor: NonNegativeNumber,
  pureWeight: NonNegativeNumber,
  pureWeightAdded: NonNegativeNumber,
  deposit: NonNegativeNumber,
  items: z.array(TransactionItemSchema),
  note: NoteSchema,
  createdBy: IdSchema,
  createdAt: DateSchema,
  updatedAt: DateSchema,
  deletedAt: DateSchema.nullable(),
  deletedBy: IdSchema.nullable(),
  clientName: z.string(),
  createdByName: z.string(),
  deletedByName: z.string().nullable(),
});

export type Transaction = z.infer<typeof TransactionSchema>;

export const TransactionListItemSchema = TransactionSchema.pick({
  id: true,
  clientId: true,
  type: true,
  laborCost: true,
  addWeightLaborCost: true,
  totalLabor: true,
  pureWeight: true,
  pureWeightAdded: true,
  deposit: true,
  items: true,
  note: true,
  createdAt: true,
  deletedAt: true,
  clientName: true,
  createdByName: true,
});

export type TransactionListItem = z.infer<typeof TransactionListItemSchema>;

export type TransactionListResponse = CursorPaginatedResponse<TransactionListItem>;

export const TransactionListResponseSchema =
  cursorPaginatedResponseSchema(TransactionListItemSchema);

export const TransactionStatsSchema = z.object({
  goldBalance: z.coerce.number(),
  goldBalanceMonth: z.coerce.number(),
  goldBalanceWeek: z.coerce.number(),
  goldBalanceDay: z.coerce.number(),
  inboundPureWeight: NonNegativeNumber,
  inboundPureWeightMonth: NonNegativeNumber,
  inboundPureWeightWeek: NonNegativeNumber,
  inboundPureWeightDay: NonNegativeNumber,
  cashBalance: z.coerce.number(),
  cashBalanceMonth: z.coerce.number(),
  cashBalanceWeek: z.coerce.number(),
  cashBalanceDay: z.coerce.number(),
  lastTransactionAt: z.string().nullable(),
});

export type TransactionStats = z.infer<typeof TransactionStatsSchema>;
